if (!customElements.get('vehicle-quickbar')) {
  class VehicleQuickbar extends HTMLElement {

    constructor() {
      super();
      this.handleYearChange = this.handleYearChange.bind(this);
      this.handleMakeChange = this.handleMakeChange.bind(this);
      this.handleModelChange = this.handleModelChange.bind(this);
      this.handleReset = this.handleReset.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleToggle = this.handleToggle.bind(this);
    }

    connectedCallback() {
      this.initElements();
      this.form.hidden = true;
      this.bindEvents();
      this.initGarage();
      this.fetchYmmData().then(() => {
        this.populateYearDropdown();
        this.syncFromURL();
        this.form.hidden = false;
      });
    }

    initElements() {
      this.yearSelect = this.querySelector('#vq-year');
      this.makeSelect = this.querySelector('#vq-make');
      this.modelSelect = this.querySelector('#vq-model');
      this.submodelSelect = this.querySelector('#vq-submodel');
      this.goBtn = this.querySelector('.vq-go-btn');
      this.resetBtn = this.querySelector('.vq-reset-btn');
      this.form = this.querySelector('.vq-form');
      this.toggleBtn = this.querySelector('.vq-toggle');
      this.expandable = this.querySelector('.vq-expandable');
      
      // Default to expanded on desktop, collapsed on mobile
      const isMobile = window.innerWidth <= 1024;
      this.isCollapsed = isMobile ? (this.dataset.collapsed === 'true') : false;
    }

    bindEvents() {
      this.yearSelect?.addEventListener('change', this.handleYearChange);
      this.makeSelect?.addEventListener('change', this.handleMakeChange);
      this.modelSelect?.addEventListener('change', this.handleModelChange);
      this.resetBtn?.addEventListener('click', this.handleReset);
      this.form?.addEventListener('submit', this.handleFormSubmit);
      this.toggleBtn?.addEventListener('click', this.handleToggle);
      
      // Initialize collapsed state
      this.updateCollapsedState();
    }

    async fetchYmmData() {
      if (!window.ymm_file_url) return;
      window.__ymmCache = window.__ymmCache || {};
      const url = window.ymm_file_url;
      if (!window.__ymmCache[url]) {
        window.__ymmCache[url] = fetch(url)
          .then(r => r.json())
          .catch(err => {
            console.error('[vehicle-quickbar] Failed to fetch YMM data:', err);
            return null;
          });
      }
      this._ymmData = await window.__ymmCache[url];
    }

    get ymmHierarchy() {
      return this._ymmData?.hierarchy || {};
    }

    get ymmYears() {
      return this._ymmData?.years || [];
    }

    populateYearDropdown() {
      if (!this.yearSelect || !this._ymmData) return;

      const years = this.ymmYears;
      this.yearSelect.innerHTML = '<option value="">Year</option>';
      years.forEach(year => {
        const option = document.createElement('option');
        option.value = year;
        option.textContent = year;
        this.yearSelect.appendChild(option);
      });
    }

    handleYearChange() {
      const year = this.yearSelect.value;
      this.makeSelect.innerHTML = '<option value="">Make</option>';
      this.modelSelect.innerHTML = '<option value="">Model</option>';
      this.submodelSelect.innerHTML = '<option value="">Submodel</option>';
      this.modelSelect.disabled = true;
      this.submodelSelect.disabled = true;

      if (!year) {
        this.makeSelect.disabled = true;
        return;
      }

      this.makeSelect.disabled = false;
      const yearData = this.ymmHierarchy[year];
      if (!yearData) return;

      Object.values(yearData).forEach(make => {
        const option = document.createElement('option');
        option.value = make.value;
        option.textContent = make.label;
        this.makeSelect.appendChild(option);
      });
    }

    handleMakeChange() {
      const year = this.yearSelect.value;
      const make = this.makeSelect.value;
      this.modelSelect.innerHTML = '<option value="">Model</option>';
      this.submodelSelect.innerHTML = '<option value="">Submodel</option>';
      this.submodelSelect.disabled = true;

      if (!make) {
        this.modelSelect.disabled = true;
        return;
      }

      this.modelSelect.disabled = false;
      const models = this.ymmHierarchy[year]?.[make]?.models;
      if (!models) return;

      Object.values(models).forEach(model => {
        const option = document.createElement('option');
        option.value = model.value;
        option.textContent = model.label;
        this.modelSelect.appendChild(option);
      });
    }

    handleModelChange() {
      const year = this.yearSelect.value;
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      this.submodelSelect.innerHTML = '<option value="">Submodel</option>';

      if (!model) {
        this.submodelSelect.disabled = true;
        return;
      }

      const submodels = this.ymmHierarchy[year]?.[make]?.models?.[model]?.submodels;

      if (!submodels || !Object.keys(submodels).length) {
        this.submodelSelect.disabled = true;
        return;
      }

      this.submodelSelect.disabled = false;
      Object.values(submodels).forEach(submodel => {
        const option = document.createElement('option');
        option.value = submodel.value;
        option.textContent = submodel.label;
        this.submodelSelect.appendChild(option);
      });
    }

    getURLParams() {
      const params = new URLSearchParams(window.location.search);
      return {
        year: params.get('year') || '',
        make: params.get('make') || '',
        model: params.get('model') || '',
        submodel: params.get('submodel') || ''
      };
    }

    syncFromURL() {
      const urlParams = this.getURLParams();
      if (!urlParams.year) return;

      // Set year and populate makes (synchronous operation)
      this.yearSelect.value = urlParams.year;
      this.handleYearChange();

      if (!urlParams.make) return;

      // Find matching make (case-insensitive) - options are already populated
      const makeOption = Array.from(this.makeSelect.options).find(
        opt => opt.value.toLowerCase() === urlParams.make.toLowerCase()
      );
      if (makeOption) {
        this.makeSelect.value = makeOption.value;
        this.handleMakeChange();

        if (!urlParams.model) return;

        // Find matching model (case-insensitive) - options are already populated
        const modelOption = Array.from(this.modelSelect.options).find(
          opt => opt.value.toLowerCase() === urlParams.model.toLowerCase()
        );
        if (modelOption) {
          this.modelSelect.value = modelOption.value;
          this.handleModelChange();

          if (!urlParams.submodel) return;

          // Find matching submodel (case-insensitive) - options are already populated
          const submodelOption = Array.from(this.submodelSelect.options).find(
            opt => opt.value.toLowerCase() === urlParams.submodel.toLowerCase()
          );
          if (submodelOption) {
            this.submodelSelect.value = submodelOption.value;
          }
        }
      }
    }

    handleReset() {
      this.yearSelect.value = '';
      this.handleYearChange();
    }

    handleFormSubmit(event) {
      event.preventDefault();
      const year = this.yearSelect.value;
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      const submodel = this.submodelSelect.value;

      if (year && make && model) {
        this.addToGarage(year, make, model, submodel);
      }

      const params = new URLSearchParams();

      if (year) params.set('year', year);
      if (make) params.set('make', make);
      if (model) params.set('model', model);
      if (submodel) params.set('submodel', submodel);

      const queryString = params.toString();
      const url = queryString
        ? `${this.form.action}?${queryString}`
        : this.form.action;

      window.location.href = url;
    }

    handleToggle(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isCollapsed = !this.isCollapsed;
      this.updateCollapsedState();
    }

    updateCollapsedState() {
      if (this.isCollapsed) {
        this.classList.add('vq-collapsed');
        this.classList.remove('vq-expanded');
        this.dataset.collapsed = 'true';
        this.toggleBtn?.setAttribute('aria-expanded', 'false');
      } else {
        this.classList.remove('vq-collapsed');
        this.classList.add('vq-expanded');
        this.dataset.collapsed = 'false';
        this.toggleBtn?.setAttribute('aria-expanded', 'true');
      }
    }

    // ── Garage ──────────────────────────────────────────────────────────────

    initGarage() {
      this.garageWrapper = this.querySelector('.vq-garage-wrapper');
      this.garageBtn = this.querySelector('.vq-garage-btn');
      this.garagePanel = this.querySelector('.vq-garage-panel');
      this.garagePanelInner = this.querySelector('.vq-garage-panel-inner');

      this.garageBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleGaragePanel();
      });

      document.addEventListener('click', (e) => {
        if (this.garagePanel && !this.garagePanel.hidden && !this.garageWrapper?.contains(e.target)) {
          this.closeGaragePanel();
        }
      });

    }

    toggleGaragePanel() {
      if (!this.garagePanel) return;
      if (this.garagePanel.hidden) {
        this.openGaragePanel();
      } else {
        this.closeGaragePanel();
      }
    }

    openGaragePanel() {
      this.renderGarageDropdown();
      this.garagePanel.hidden = false;
      this.garageBtn?.setAttribute('aria-expanded', 'true');
      this.garageBtn?.classList.add('vq-garage-open');
      this.classList.add('vq-garage-panel-open');
    }

    closeGaragePanel() {
      if (!this.garagePanel) return;
      this.garagePanel.hidden = true;
      this.garageBtn?.setAttribute('aria-expanded', 'false');
      this.garageBtn?.classList.remove('vq-garage-open');
      this.classList.remove('vq-garage-panel-open');
    }

    loadGarage() {
      try {
        return JSON.parse(localStorage.getItem('vq_garage') || '[]');
      } catch {
        return [];
      }
    }

    saveGarage(cars) {
      localStorage.setItem('vq_garage', JSON.stringify(cars));
    }

    addToGarage(year, make, model, submodel) {
      const cars = this.loadGarage();
      const sub = submodel || '';
      const exists = cars.some(
        c => c.year === year && c.make === make && c.model === model && c.submodel === sub
      );
      if (exists) return;

      const makeLabel = this.makeSelect?.options[this.makeSelect.selectedIndex]?.text || make;
      const modelLabel = this.modelSelect?.options[this.modelSelect.selectedIndex]?.text || model;
      const submodelLabel = sub
        ? (this.submodelSelect?.options[this.submodelSelect.selectedIndex]?.text || sub)
        : '';

      cars.push({
        year,
        make,
        model,
        submodel: sub,
        label: [year, makeLabel, modelLabel, submodelLabel].filter(Boolean).join(' ')
      });
      this.saveGarage(cars);
    }

    removeFromGarage(index) {
      const cars = this.loadGarage();
      cars.splice(index, 1);
      this.saveGarage(cars);
      this.renderGarageDropdown();
    }

    renderGarageDropdown() {
      if (!this.garagePanelInner) return;
      const cars = this.loadGarage();
      this.garagePanelInner.innerHTML = '';

      if (cars.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'vq-garage-empty';
        empty.innerHTML = 'No saved vehicles yet.<br>Select a vehicle and click GO to save it.';
        this.garagePanelInner.appendChild(empty);
      } else {
        const list = document.createElement('ul');
        list.className = 'vq-garage-list';
        cars.forEach((car, i) => {
          const item = document.createElement('li');
          item.className = 'vq-garage-item';

          const selectBtn = document.createElement('button');
          selectBtn.type = 'button';
          selectBtn.className = 'vq-garage-select-btn';
          selectBtn.textContent = car.label;
          selectBtn.addEventListener('click', () => this.selectGarageVehicle(car));

          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'vq-garage-delete-btn';
          deleteBtn.setAttribute('aria-label', 'Remove vehicle');
          deleteBtn.innerHTML = '&times;';
          deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.removeFromGarage(i);
          });

          item.appendChild(selectBtn);
          item.appendChild(deleteBtn);
          list.appendChild(item);
        });
        this.garagePanelInner.appendChild(list);
      }

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'vq-garage-add-btn';
      addBtn.textContent = '+ Add New Vehicle';
      addBtn.addEventListener('click', () => {
        this.handleReset();
        this.closeGaragePanel();
        if (window.innerWidth <= 1024) {
          this.isCollapsed = false;
          this.updateCollapsedState();
        }
      });
      this.garagePanelInner.appendChild(addBtn);
    }

    selectGarageVehicle(car) {
      this.closeGaragePanel();
      const params = new URLSearchParams();
      if (car.year) params.set('year', car.year);
      if (car.make) params.set('make', car.make);
      if (car.model) params.set('model', car.model);
      if (car.submodel) params.set('submodel', car.submodel);

      const currentPath = window.location.pathname;
      const isFilterablePage = currentPath.includes('/collections/') || currentPath.includes('/search');
      const basePath = isFilterablePage ? currentPath : (this.form?.action || '/collections/all');

      window.location.href = `${basePath}?${params.toString()}`;
    }
  }

  customElements.define('vehicle-quickbar', VehicleQuickbar);
}
